import type { TeammateId } from "@repo/app-types/entities/teammate";
import { useEffect, type FC } from "react";
import { Outlet, useMatch } from "react-router";
import { twMerge } from "tailwind-merge";
import { appPaths } from "../../../app/appPaths.js";
import { FullHeightPageContent } from "../../../components/layout/pageLayout.js";
import { useCopilotProps } from "../../copilot/copilotContext.js";
import { AgentConfigToolbar } from "../agentConfigToolbar.js";
import { AgentSimulationSlideover } from "../agentSimulationSlideover.js";
import { useTeammateAgentConfigController } from "../useTeammateAgentConfigController.js";
import { StaticAgentConfigProvider } from "./staticAgentConfigContext.js";

export interface StaticAgentConfigLayoutProps {
  teammateAgentId: TeammateId;
}

/**
 * Layout for a static agent: the active tab content (Performance, Calls,
 * Instructions, Configuration, Testing) swaps inside the `<Outlet />`.
 * The toolbar (Try this agent / Publish / …) shows only on Instructions and
 * Configuration. Performance and Calls are read-only and render like a
 * regular agent's tabs. The Instructions and Configuration tabs consume the
 * controller via context.
 */
export const StaticAgentConfigLayout: FC<StaticAgentConfigLayoutProps> = ({
  teammateAgentId,
}) => {
  const controller = useTeammateAgentConfigController(teammateAgentId);
  const { flushAgentConfigSave, flushPlaybookSave } = controller;

  // Ask Alpharun runs these before each message it sends. Its voice agent
  // tools read the agent config and playbook drafts from the database, and
  // autosave is debounced, so an edit made moments ago is saved first.
  const registerBeforeSend = useCopilotProps()?.registerBeforeSend;
  useEffect(
    () => registerBeforeSend?.(flushAgentConfigSave),
    [registerBeforeSend, flushAgentConfigSave],
  );
  useEffect(
    () => registerBeforeSend?.(flushPlaybookSave),
    [registerBeforeSend, flushPlaybookSave],
  );

  // Call each matcher unconditionally so hook order stays stable, then combine.
  const isInstructionsTab = useMatch(
    appPaths.teammateAgentInstructions.pattern,
  );
  const isResourcesOutputsTab = useMatch(
    appPaths.teammateAgentResourcesOutputs.pattern,
  );
  const isTestingTab = useMatch(`${appPaths.teammateAgentTesting.pattern}/*`);
  const isConfigTab = Boolean(
    isInstructionsTab || isResourcesOutputsTab || isTestingTab,
  );
  const showToolbar = Boolean(isInstructionsTab || isResourcesOutputsTab);

  // Performance/Calls carry no config chrome, so render them the same way a
  // regular agent's tabs are rendered — each tab owns its layout (sticky
  // filter bar + PageBody) inside the page's scroll region, not the
  // full-width editing layout below.
  const content = !isConfigTab ? (
    <Outlet />
  ) : (
    // Declared full height so the page shell provides a bounded scroll
    // region this fills exactly; inner areas scroll instead of the page —
    // otherwise the page scrolls and the toolbar scrolls out of view.
    <FullHeightPageContent>
      <div className="flex h-full min-w-[1100px] flex-col">
        {showToolbar ? <AgentConfigToolbar controller={controller} /> : null}
        <div className="flex min-h-0 flex-1">
          {/* Instructions/Resources are full-bleed editing surfaces that
              manage their own padding; Testing is a normal content tab that
              brings its own PageBody padding and scrolls here so the toolbar
              stays put. `relative`: react-aria's hidden inputs must count
              toward this pane's overflow, not the page shell's. */}
          <div
            className={twMerge(
              "relative min-w-0 flex-1",
              isTestingTab ? "overflow-y-auto" : "overflow-hidden",
            )}
          >
            <Outlet />
          </div>
        </div>

        <AgentSimulationSlideover controller={controller} />
      </div>
    </FullHeightPageContent>
  );

  return (
    <StaticAgentConfigProvider controller={controller}>
      {content}
    </StaticAgentConfigProvider>
  );
};
